import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import API from '@/config/api';
import {
  CheckCircle, Send, ArrowRight, Phone, Mail, User, Calendar, Clock, FileText
} from 'lucide-react';

const heading = "font-['Cormorant_Garamond',serif]";
const body = "font-['Outfit',sans-serif]";

const serviceOptions = [
  { value: "Credit Building", label: "Credit Building (Free)" },
  { value: "Debt Help", label: "Debt Help (Free)" },
  { value: "Personal Loans", label: "Personal Loans (Free)" },
  { value: "Business Funding", label: "Business Funding (Free)" },
  { value: "Tax Preparation", label: "Tax Preparation (Free)" },
  { value: "Estate Planning", label: "Estate Planning (Free)" },
  { value: "Business Registration", label: "Business Registration (Free)" },
  { value: "Life Insurance", label: "Life Insurance (Free)" },
  { value: "Business Consultation", label: "Business Consultation ($50/hr)" },
  { value: "Artist Management", label: "Artist Management ($50/hr)" },
  { value: "Branding", label: "Branding ($50/hr)" },
  { value: "Websites", label: "Websites ($50/hr)" },
  { value: "Photography & Fine Art", label: "Photography & Fine Art ($50/hr)" },
  { value: "Legacy Blueprint Call", label: "Legacy Blueprint Call (Free)" },
  { value: "Wealth Strategy Session", label: "Wealth Strategy Session ($297)" },
  { value: "Empire Building Intensive", label: "Empire Building Intensive ($750)" },
  { value: "Dynasty Advisory", label: "Dynasty Advisory ($2,500)" },
];

const timeSlots = [
  "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"
];

export default function BookingPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    service_type: '',
    preferred_date: '',
    preferred_time: '',
    message: '',
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.full_name || !form.email || !form.phone || !form.service_type || !form.preferred_date || !form.preferred_time) {
      toast.error('Please fill out all required fields.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/consultation/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        toast.error(data.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className={`min-h-screen bg-[#0a0a0f] ${body} flex items-center justify-center px-6`} data-testid="booking-page">
        <div className="max-w-md w-full text-center py-24">
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-emerald-400" />
          </div>
          <h1 className={`${heading} text-3xl sm:text-4xl font-light text-[#e9dcc9] mb-4`}>
            Request <span className="text-[#D4AF37]">Received</span>
          </h1>
          <p className="text-[#a39ba3] mb-2">
            We've sent a confirmation to <strong className="text-[#e9dcc9]">{form.email}</strong>.
          </p>
          <p className="text-[#a39ba3] text-sm mb-8">
            Our team will reach out within 24 hours to confirm your consultation and discuss next steps, including payment if applicable.
          </p>
          <Button
            onClick={() => { setSubmitted(false); setForm({ full_name: '', email: '', phone: '', service_type: '', preferred_date: '', preferred_time: '', message: '' }); }}
            className="bg-[#D4AF37] text-[#0a0a0f] font-semibold hover:bg-[#f2c94c] px-8 py-6"
            data-testid="submit-another-btn"
          >
            Submit Another Request
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-[#0a0a0f] ${body} text-[#a39ba3]`} data-testid="booking-page">
      <SEO title="Book a Consultation | Nouveau Riche Legacy" description="Request a consultation with Nouveau Riche Legacy. Financial, business, and creative services." />

      <div className="max-w-2xl mx-auto px-6 pt-28 pb-24">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Consultations</p>
          <h1 className={`${heading} text-4xl sm:text-5xl font-light tracking-tight text-[#e9dcc9] mb-4`}>
            Book a <span className="text-[#D4AF37]">Consultation</span>
          </h1>
          <p className="text-base text-[#a39ba3] max-w-md mx-auto leading-relaxed">
            Fill out the form below and we'll get back to you to confirm your session. Payment is handled after booking.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="consultation-form">
          {/* Name */}
          <div>
            <label className="flex items-center gap-2 text-sm text-[#e9dcc9] mb-2 font-medium">
              <User className="h-4 w-4 text-[#D4AF37]" /> Full Name <span className="text-red-400">*</span>
            </label>
            <Input
              value={form.full_name}
              onChange={(e) => update('full_name', e.target.value)}
              placeholder="Your full name"
              className="bg-[#1c1814] border-[#2d2722] text-[#e9dcc9] placeholder:text-[#a39ba3]/40 focus:border-[#D4AF37] h-12"
              required
              data-testid="input-full-name"
            />
          </div>

          {/* Email + Phone row */}
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="flex items-center gap-2 text-sm text-[#e9dcc9] mb-2 font-medium">
                <Mail className="h-4 w-4 text-[#D4AF37]" /> Email <span className="text-red-400">*</span>
              </label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                placeholder="you@email.com"
                className="bg-[#1c1814] border-[#2d2722] text-[#e9dcc9] placeholder:text-[#a39ba3]/40 focus:border-[#D4AF37] h-12"
                required
                data-testid="input-email"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm text-[#e9dcc9] mb-2 font-medium">
                <Phone className="h-4 w-4 text-[#D4AF37]" /> Phone <span className="text-red-400">*</span>
              </label>
              <Input
                type="tel"
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="bg-[#1c1814] border-[#2d2722] text-[#e9dcc9] placeholder:text-[#a39ba3]/40 focus:border-[#D4AF37] h-12"
                required
                data-testid="input-phone"
              />
            </div>
          </div>

          {/* Service Type */}
          <div>
            <label className="flex items-center gap-2 text-sm text-[#e9dcc9] mb-2 font-medium">
              <FileText className="h-4 w-4 text-[#D4AF37]" /> Service Interested In <span className="text-red-400">*</span>
            </label>
            <Select value={form.service_type} onValueChange={(v) => update('service_type', v)}>
              <SelectTrigger className="bg-[#1c1814] border-[#2d2722] text-[#e9dcc9] h-12 focus:border-[#D4AF37]" data-testid="select-service">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent className="bg-[#1c1814] border-[#2d2722]">
                {serviceOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className="text-[#e9dcc9] focus:bg-[#D4AF37]/10 focus:text-[#D4AF37]">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date + Time row */}
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="flex items-center gap-2 text-sm text-[#e9dcc9] mb-2 font-medium">
                <Calendar className="h-4 w-4 text-[#D4AF37]" /> Preferred Date <span className="text-red-400">*</span>
              </label>
              <Input
                type="date"
                value={form.preferred_date}
                onChange={(e) => update('preferred_date', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="bg-[#1c1814] border-[#2d2722] text-[#e9dcc9] focus:border-[#D4AF37] h-12"
                required
                data-testid="input-date"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm text-[#e9dcc9] mb-2 font-medium">
                <Clock className="h-4 w-4 text-[#D4AF37]" /> Preferred Time <span className="text-red-400">*</span>
              </label>
              <Select value={form.preferred_time} onValueChange={(v) => update('preferred_time', v)}>
                <SelectTrigger className="bg-[#1c1814] border-[#2d2722] text-[#e9dcc9] h-12 focus:border-[#D4AF37]" data-testid="select-time">
                  <SelectValue placeholder="Select a time" />
                </SelectTrigger>
                <SelectContent className="bg-[#1c1814] border-[#2d2722]">
                  {timeSlots.map((slot) => (
                    <SelectItem key={slot} value={slot} className="text-[#e9dcc9] focus:bg-[#D4AF37]/10 focus:text-[#D4AF37]">
                      {slot}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="flex items-center gap-2 text-sm text-[#e9dcc9] mb-2 font-medium">
              <ArrowRight className="h-4 w-4 text-[#D4AF37]" /> What would you like to discuss?
            </label>
            <Textarea
              value={form.message}
              onChange={(e) => update('message', e.target.value)}
              placeholder="Tell us a little about your goals, situation, or questions..."
              rows={4}
              className="bg-[#1c1814] border-[#2d2722] text-[#e9dcc9] placeholder:text-[#a39ba3]/40 focus:border-[#D4AF37] resize-none"
              data-testid="input-message"
            />
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#D4AF37] text-[#0a0a0f] font-semibold tracking-wide hover:bg-[#f2c94c] h-14 text-base transition-all duration-300"
            data-testid="submit-consultation-btn"
          >
            {submitting ? 'Submitting...' : 'Submit Consultation Request'}
            {!submitting && <Send className="ml-2 h-4 w-4" />}
          </Button>

          <p className="text-center text-xs text-[#a39ba3]/50 mt-4">
            Payment is handled after your consultation is confirmed. Free services have no charge.
          </p>
        </form>
      </div>
    </div>
  );
}
